import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ArrowRightLeft, Loader2, RefreshCw } from "lucide-react";

const CURRENCY_NAMES = {
  EUR: "Euro",
  USD: "US Dollar",
  GBP: "British Pound",
  CHF: "Swiss Franc",
  JPY: "Japanese Yen",
  AUD: "Australian Dollar",
  CAD: "Canadian Dollar",
  SEK: "Swedish Krona",
  NOK: "Norwegian Krone",
  DKK: "Danish Krone",
  PLN: "Polish Zloty",
  CZK: "Czech Koruna",
  HUF: "Hungarian Forint",
  TRY: "Turkish Lira",
  BRL: "Brazilian Real",
  CNY: "Chinese Yuan",
  INR: "Indian Rupee",
  KRW: "South Korean Won",
  THB: "Thai Baht",
  MXN: "Mexican Peso",
  NZD: "New Zealand Dollar",
  SGD: "Singapore Dollar",
  HKD: "Hong Kong Dollar",
  ZAR: "South African Rand",
  AED: "UAE Dirham",
  SAR: "Saudi Riyal",
  RUB: "Russian Ruble",
  ILS: "Israeli Shekel",
};

const POPULAR_CURRENCIES = Object.keys(CURRENCY_NAMES);

export const CurrencyConverter = () => {
  const [amount, setAmount] = useState("100");
  const [from, setFrom] = useState("EUR");
  const [to, setTo] = useState("USD");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rate, setRate] = useState(null);

  useEffect(() => {
    if (amount && parseFloat(amount) > 0) {
      convert();
    }
  }, [from, to]);

  const convert = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/currency/convert?amount=${val}&from_currency=${from}&to_currency=${to}`,
        { withCredentials: true }
      );
      setResult(res.data.converted);
      setRate(res.data.rate);
    } catch {
      setResult(null);
      setRate(null);
    } finally {
      setLoading(false);
    }
  };

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  const handleAmountChange = (e) => {
    setAmount(e.target.value);
  };

  const handleAmountBlur = () => {
    if (amount && parseFloat(amount) > 0) convert();
  };

  const renderCurrencySelect = (value, onChange, testId) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="glass-input flex-1 h-10" data-testid={testId}>
        <SelectValue>
          <span className="flex items-center gap-1.5">
            <span className="font-semibold text-white">{value}</span>
            <span className="text-xs text-zinc-500 hidden sm:inline">{CURRENCY_NAMES[value]}</span>
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="glass-card border-white/10 max-h-60">
        {POPULAR_CURRENCIES.map((c) => (
          <SelectItem key={c} value={c}>
            <span className="flex items-center gap-2">
              <span className="font-semibold text-sm w-10">{c}</span>
              <span className="text-zinc-400 text-sm">{CURRENCY_NAMES[c]}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="glass-card p-4" data-testid="currency-converter">
      <div className="flex items-center gap-2 mb-3">
        <ArrowRightLeft className="w-4 h-4 text-teal-400" />
        <h3 className="text-sm font-semibold text-white">Currency Converter</h3>
      </div>

      <div className="space-y-3">
        {/* From row */}
        <div className="flex gap-2 items-center">
          <Input
            type="number"
            step="0.01"
            min="0"
            className="glass-input w-28 h-10 text-base font-medium"
            value={amount}
            onChange={handleAmountChange}
            onBlur={handleAmountBlur}
            onKeyDown={(e) => e.key === "Enter" && convert()}
            data-testid="converter-amount-input"
          />
          {renderCurrencySelect(from, setFrom, "converter-from-select")}
        </div>

        {/* Swap button + rate */}
        <div className="flex items-center justify-between">
          <button
            onClick={swap}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
            data-testid="converter-swap-btn"
          >
            <RefreshCw className="w-4 h-4 text-teal-400" />
          </button>
          {rate && (
            <span className="text-xs text-zinc-500" data-testid="converter-rate">
              1 {from} = {rate.toFixed(4)} {to}
            </span>
          )}
        </div>

        {/* To row */}
        <div className="flex gap-2 items-center">
          <div className="glass-input w-28 h-10 flex items-center px-3 text-base font-medium text-white" data-testid="converter-result">
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-teal-400" />
            ) : result !== null ? (
              result.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            ) : (
              <span className="text-zinc-600">0.00</span>
            )}
          </div>
          {renderCurrencySelect(to, setTo, "converter-to-select")}
        </div>
      </div>
    </div>
  );
};
